import { jsx as _jsx } from "react/jsx-runtime";
import { useBox } from 'shared/hooks/utils';
import { isDeviceUISettingsEqual } from 'console/shared/content/ui-settings/utils';
import { isEqual } from 'lodash';
import { memo, useCallback, useContext, useEffect, useMemo, useState, } from 'react';
import { useDeviceConfig } from '../../device/config';
import { DeviceUISettingsControl } from './types';
import { useBooleanState } from 'shared/hooks/state/boolean';
export const DeviceUISettingsControlProvider = memo(function DeviceUISettingsControlProvider(props) {
    const { setIsDeviceUiSettingsChanged, setIsDeviceUiSettingsCancel, tab_config, setCustomPanels, setTabConfig, children, custom_panels, sorting_mode, device_ui_settings, getTabConfig, } = props;
    const { tab_key } = useDeviceConfig();
    const initial_device_ui_settings = useMemo(() => device_ui_settings, [device_ui_settings]);
    const [grouping_panel_key, setGroupingPanelKey] = useState(null);
    const boxed_panel_config = useBox(tab_config.panels_config);
    const boxed_panels = useBox(tab_config.panels);
    const onDeleteCustomPanel = useCallback((panel_key) => {
        setCustomPanels((prev_panels) => {
            const filtered_panels = Object.entries(prev_panels).filter(([key]) => panel_key !== key);
            const panels = Object.fromEntries(filtered_panels);
            return panels;
        });
    }, []);
    const onChangeCustomPanelName = useCallback((panel_key, name) => {
        setCustomPanels((prev_panels) => {
            const panel = prev_panels?.[panel_key];
            return {
                ...prev_panels,
                [panel_key]: {
                    ...panel,
                    name,
                },
            };
        });
    }, []);
    const onAddCustomPanel = useCallback((name) => {
        setCustomPanels((prev_panels) => {
            const panel_key = name;
            const position = Object.keys(prev_panels).length;
            const panel = {
                name,
                show: true,
                position,
                internal_order: {},
            };
            setGroupingPanelKey(panel_key);
            return {
                ...prev_panels,
                [panel_key]: panel,
            };
        });
    }, []);
    const onVisibilityPanelChange = useCallback((panel_key) => {
        const panels = boxed_panels.current.map((panel) => {
            if (panel.id === panel_key) {
                return {
                    ...panel,
                    ui_settings: {
                        ...panel.ui_settings,
                        show: !panel.ui_settings.show,
                    },
                };
            }
            return panel;
        });
        const panels_ui_settings = Object.fromEntries(panels.map(({ id, ui_settings }) => {
            return [id, ui_settings];
        }));
        setTabConfig(({ panels_config: panels_content, device_ui_settings: prev_device_ui_settings, }) => {
            const device_ui_settings = {
                ...prev_device_ui_settings,
                [tab_key]: {
                    ...prev_device_ui_settings[tab_key],
                    ...panels_ui_settings,
                },
            };
            setIsDeviceUiSettingsChanged();
            if (isDeviceUISettingsEqual(initial_device_ui_settings, device_ui_settings, tab_key)) {
                setIsDeviceUiSettingsCancel();
            }
            return {
                panels_config: panels_content,
                panels,
                device_ui_settings,
            };
        });
    }, [setTabConfig, initial_device_ui_settings, device_ui_settings, tab_key]);
    const [is_panels_wrapped, unwrapPanels, , togglePanelsWrap] = useBooleanState(false);
    useEffect(() => {
        if (!sorting_mode) {
            unwrapPanels();
        }
    }, [sorting_mode]);
    const onPanelsDragEnd = useCallback((panels) => {
        if (isEqual(boxed_panels.current, panels)) {
            return;
        }
        setTabConfig(({ panels_config: panels_content, device_ui_settings: prev_device_ui_settings, }) => {
            const reordered_panels = Object.entries(prev_device_ui_settings?.[tab_key] || {}).map(([id, setting]) => [
                id,
                {
                    ...setting,
                    position: panels.findIndex((panel) => panel.id == id) ?? -1,
                },
            ]);
            const tab = Object.fromEntries(reordered_panels);
            const device_ui_settings = {
                ...prev_device_ui_settings,
                [tab_key]: tab,
            };
            setIsDeviceUiSettingsChanged();
            if (isDeviceUISettingsEqual(initial_device_ui_settings, device_ui_settings, tab_key)) {
                setIsDeviceUiSettingsCancel();
            }
            return {
                panels_config: panels_content,
                panels,
                device_ui_settings,
            };
        });
    }, [setTabConfig, device_ui_settings, initial_device_ui_settings, tab_key]);
    const onReorderCards = useCallback((panel_key, cards) => {
        const prev_cards = boxed_panel_config.current[panel_key];
        if (isEqual(prev_cards, cards)) {
            return;
        }
        const internal_order = Object.fromEntries(cards.map(({ id, ui_settings }, index) => {
            return [id, { ...ui_settings, position: index }];
        }));
        setTabConfig(({ device_ui_settings: prev_device_ui_settings, panels_config: prev_panels_content, }) => {
            const panels_content = {
                ...prev_panels_content,
                [panel_key]: cards,
            };
            const device_ui_settings = {
                ...prev_device_ui_settings,
                [tab_key]: {
                    ...prev_device_ui_settings[tab_key],
                    [panel_key]: {
                        ...prev_device_ui_settings[tab_key]?.[panel_key],
                        internal_order,
                    },
                },
            };
            const tab_config = getTabConfig(device_ui_settings);
            setIsDeviceUiSettingsChanged();
            if (isDeviceUISettingsEqual(initial_device_ui_settings, device_ui_settings, tab_key)) {
                setIsDeviceUiSettingsCancel();
            }
            return { ...tab_config, panels_config: panels_content };
        });
    }, [
        setTabConfig,
        initial_device_ui_settings,
        device_ui_settings,
        getTabConfig,
        custom_panels,
        tab_key,
    ]);
    const onVisibilityCardChange = useCallback((panel_key, changed_id) => {
        const cards = boxed_panel_config.current[panel_key];
        if (!cards) {
            return;
        }
        const cards_to_internal_order = Object.fromEntries(cards.map(({ id, ui_settings }, index) => {
            const { show } = ui_settings;
            if (id == changed_id) {
                return [id, { position: index, show: !show }];
            }
            return [id, { position: index, show }];
        }));
        setTabConfig(({ device_ui_settings: prev_device_ui_settings, panels_config: prev_panels_config, }) => {
            const panel_config = cards.map((card) => {
                const { id, ui_settings } = card;
                const { show } = ui_settings;
                if (id != changed_id) {
                    return card;
                }
                return {
                    ...card,
                    ui_settings: {
                        ...ui_settings,
                        show: !show,
                    },
                };
            });
            const panels_config = {
                ...prev_panels_config,
                [panel_key]: panel_config,
            };
            const device_ui_settings = {
                ...prev_device_ui_settings,
                [tab_key]: {
                    ...prev_device_ui_settings[tab_key],
                    [panel_key]: {
                        ...prev_device_ui_settings[tab_key]?.[panel_key],
                        internal_order: cards_to_internal_order,
                    },
                },
            };
            const tab_config = getTabConfig(device_ui_settings);
            setIsDeviceUiSettingsChanged();
            if (isDeviceUISettingsEqual(initial_device_ui_settings, device_ui_settings, tab_key)) {
                setIsDeviceUiSettingsCancel();
            }
            return { ...tab_config, panels_config };
        });
    }, [
        setTabConfig,
        initial_device_ui_settings,
        device_ui_settings,
        custom_panels,
        getTabConfig,
        tab_key,
    ]);
    const onAddCardToCustomPanel = useCallback((panel_key, card_id) => {
        setCustomPanels((prev_panels) => {
            const panel = prev_panels?.[panel_key];
            const { internal_order: panel_internal_order } = panel || {};
            const card_position = Object.keys(panel_internal_order || {}).length;
            const internal_order = {
                ...panel_internal_order,
                [card_id]: {
                    show: true,
                    position: card_position,
                },
            };
            return {
                ...prev_panels,
                [panel_key]: {
                    ...panel,
                    internal_order,
                },
            };
        });
    }, []);
    const onPanelInteractionEnd = useCallback((layout) => {
        setTabConfig((prev_tab_config) => {
            const { device_ui_settings: prev_device_ui_settings, panels_config } = prev_tab_config;
            const device_ui_settings_panels = prev_device_ui_settings[tab_key];
            if (device_ui_settings_panels) {
                const new_layout = Object.fromEntries(layout.map(({ i: panel_key, h: height, w: width, x, y }) => [
                    panel_key,
                    {
                        ...device_ui_settings_panels[panel_key],
                        x,
                        y,
                        width,
                        height,
                    },
                ]));
                const device_ui_settings = {
                    ...prev_device_ui_settings,
                    [tab_key]: {
                        ...device_ui_settings_panels,
                        ...new_layout,
                    },
                };
                const tab_config = getTabConfig(device_ui_settings);
                setIsDeviceUiSettingsChanged();
                if (isDeviceUISettingsEqual(initial_device_ui_settings, device_ui_settings, tab_key)) {
                    setIsDeviceUiSettingsCancel();
                }
                return {
                    ...tab_config,
                    panels_config,
                };
            }
            return prev_tab_config;
        });
    }, [tab_key, initial_device_ui_settings, getTabConfig]);
    const contentContext = useMemo(() => ({
        onReorderCards,
        onVisibilityCardChange,
        onVisibilityPanelChange,
        onPanelInteractionEnd,
        onPanelsDragEnd,
        is_panels_wrapped,
        togglePanelsWrap,
        grouping_panel_key,
        onAddCardToCustomPanel,
        onAddCustomPanel,
        setGroupingPanelKey,
        onDeleteCustomPanel,
        onChangeCustomPanelName,
    }), [
        onReorderCards,
        onVisibilityCardChange,
        onVisibilityPanelChange,
        onPanelInteractionEnd,
        onPanelsDragEnd,
        is_panels_wrapped,
        togglePanelsWrap,
        grouping_panel_key,
        onAddCardToCustomPanel,
        onAddCustomPanel,
        setGroupingPanelKey,
        onDeleteCustomPanel,
        onChangeCustomPanelName,
    ]);
    return (_jsx(DeviceUISettingsControl.Provider, { value: contentContext, children: children }));
});
export const useDeviceUISettingsControl = () => useContext(DeviceUISettingsControl);
